import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Stethoscope, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const fmtRs = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function Doctors() {
  const [items, setItems] = useState([]);
  const [report, setReport] = useState([]);
  const [name, setName] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [phone, setPhone] = useState("");

  const load = () => {
    api.get("/doctors").then(({ data }) => setItems(data)).catch(() => {});
    api.get("/reports/doctors").then(({ data }) => setReport(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name) return toast.error("Enter doctor's name");
    try {
      await api.post("/doctors", { name, specialty, phone });
      toast.success("Doctor added");
      setName(""); setSpecialty(""); setPhone("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.name}?`)) return;
    await api.delete(`/doctors/${d.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-5" data-testid="doctors-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
          <Stethoscope className="w-6 h-6 text-emerald-700" /> Doctors
        </h1>
        <p className="text-sm text-slate-500">Doctor list and doctor-wise sales report.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-4">
          <Card className="p-5 border-slate-200">
            <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Plus className="w-4 h-4 text-emerald-700" /> Add Doctor</h3>
            <form onSubmit={add} className="space-y-3">
              <div><Label className="text-xs">Name *</Label><Input data-testid="doctor-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-10" placeholder="Dr. Sharma" /></div>
              <div><Label className="text-xs">Specialty</Label><Input value={specialty} onChange={(e) => setSpecialty(e.target.value)} className="mt-1 h-10" placeholder="Physician / Cardiologist" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 h-10" /></div>
              <Button type="submit" data-testid="add-doctor-btn" className="w-full bg-emerald-900 hover:bg-emerald-800">Add Doctor</Button>
            </form>
          </Card>

          <Card className="border-slate-200">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Doctor</TableHead><TableHead>Specialty</TableHead><TableHead>Phone</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
                <TableBody>
                  {items.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell className="font-semibold">{d.name}</TableCell>
                      <TableCell className="text-sm">{d.specialty || "—"}</TableCell>
                      <TableCell className="font-mono-num text-sm">{d.phone || "—"}</TableCell>
                      <TableCell className="text-right"><Button size="icon" variant="ghost" onClick={() => remove(d)}><Trash2 className="w-4 h-4 text-red-600" /></Button></TableCell>
                    </TableRow>
                  ))}
                  {items.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-400">No doctors yet</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </Card>
        </div>

        <Card className="p-6 border-slate-200 h-fit" data-testid="doctor-report-card">
          <h3 className="font-display font-bold">Doctor-wise Sale</h3>
          <p className="text-xs text-slate-500">Based on the doctor field on bills (fill it in POS)</p>
          <div className="mt-4 space-y-2">
            {report.map((r, i) => (
              <div key={r.doctor} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0" data-testid={`doctor-report-${i}`}>
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-900 text-xs font-bold flex items-center justify-center">{i + 1}</div>
                  <div>
                    <div className="font-semibold text-sm">{r.doctor}</div>
                    <div className="text-xs text-slate-500">{r.bills} referrals</div>
                  </div>
                </div>
                <div className="font-mono-num font-bold text-emerald-900">{fmtRs(r.revenue)}</div>
              </div>
            ))}
            {report.length === 0 && <p className="text-sm text-slate-400">No doctor referral sales yet.</p>}
          </div>
        </Card>
      </div>
    </div>
  );
}
